import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';
import './Privacy.css';

/**
 * Returns and claims (cabana.claims, migration 1782900000058), 2026-09-26.
 *
 * Mike: the legal documents are the spec and the system changes to meet them. The consumer Terms of
 * Sale (sections 10 to 12) and the partner Returns & Refunds Policy promise returns of unopened
 * product, replacement of damaged, lost and wrong items, a limited warranty for consumers, a reply
 * within 10 business days, and a refund within 10 business days of receiving a return. Customers
 * file at cabanafilters.com/account/returns and partners from an order in the partner portal.
 *
 * This page is the staff side: every claim with its derived status and deadlines, and one claim at
 * a time with its photos (signed URLs from the MCP), its decisions, and the actions that apply to
 * its state. Every action is one append-only decision, audited, and emailed to the customer.
 */

type Status = 'submitted' | 'approved' | 'awaiting_return' | 'denied' | 'resolved';
type ClaimType = 'return_unopened' | 'wrong_item' | 'damaged' | 'lost' | 'warranty';

interface ClaimRow {
  id: number;
  channel: 'consumer' | 'partner';
  claim_type: ClaimType;
  status: Status;
  created_at: string;
  delivered_on: string | null;
  window_ends_on: string;
  filter_count: number;
  items: Array<{ description?: string; quantity?: number }>;
  description: string;
  preferred_remedy: string | null;
  customer_name: string | null;
  customer_email: string | null;
  dealer_name: string | null;
  shipment_id: number | null;
  dealer_order_id: number | null;
  photo_count: number;
  refunded_usd: number;
  respond_by: string;
  refund_by: string | null;
}

interface Decision {
  id: number;
  action: 'approved' | 'denied' | 'label_sent' | 'return_received' | 'refunded' | 'replaced';
  note: string | null;
  return_required: boolean | null;
  cabana_error: boolean | null;
  decided_at: string;
  decided_by_label: string | null;
  refund_amount_usd: number | null;
  stripe_refund_id: string | null;
  tax_reversal_id: string | null;
  tax_reversal_note: string | null;
  replacement_shipment_id: string | null;
  replacement_dealer_order_id: string | null;
  label_carrier: string | null;
  label_tracking_number: string | null;
  label_cost_usd: string | null;
  email_message_id: string | null;
}

interface ClaimDetail {
  claim: ClaimRow & {
    type_label: string;
    ordered_description: string | null;
    received_description: string | null;
    attested_at: string;
    box_number: number | null;
    shipped_at: string | null;
    subscription_id: number | null;
  };
  decisions: Decision[];
  photos: Array<{ id: number; content_type: string; size_bytes: number; url: string | null; url_problem?: string | null }>;
  remedies: { refund: boolean; replacement: boolean };
  payment: { payment_intent: string | null; tax_transaction: string | null; paid_usd: number; refunded_usd: number; suggested_refund_usd: number };
  respond_by: string;
  refund_by: string | null;
}

const STATUS_LABEL: Record<Status, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
  awaiting_return: 'Awaiting return',
  denied: 'Denied',
  resolved: 'Resolved',
};
const TYPE_LABEL: Record<ClaimType, string> = {
  return_unopened: 'Unopened return',
  wrong_item: 'Wrong item / size',
  damaged: 'Damaged',
  lost: 'Lost box',
  warranty: 'Warranty',
};
const ACTION_LABEL: Record<Decision['action'], string> = {
  approved: 'Approved',
  denied: 'Denied',
  label_sent: 'Return label sent',
  return_received: 'Return received',
  refunded: 'Refunded',
  replaced: 'Replacement sent',
};

const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');
const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number | string | null) => (n === null ? '—' : `$${Number(n).toFixed(2)}`);

function statusTag(s: Status) {
  const mod = s === 'resolved' ? ' records__tag--ok' : s === 'denied' || s === 'submitted' ? ' records__tag--warn' : '';
  return <span className={`records__tag${mod}`}>{STATUS_LABEL[s] ?? s}</span>;
}

function dueTag(r: Pick<ClaimRow, 'status' | 'respond_by' | 'refund_by'>) {
  if (r.status === 'submitted') {
    const late = r.respond_by < today();
    return <span className={`records__tag${late ? ' records__tag--warn' : ''}`}>Reply by {r.respond_by}</span>;
  }
  if (r.refund_by && r.status !== 'resolved' && r.status !== 'denied') {
    const late = r.refund_by < today();
    return <span className={`records__tag${late ? ' records__tag--warn' : ''}`}>Refund by {r.refund_by}</span>;
  }
  return '—';
}

const COLUMNS: Column<ClaimRow>[] = [
  { key: 'id', header: 'Claim', sortable: true, numeric: true, render: (r) => `#${r.id}` },
  { key: 'created_at', header: 'Filed', sortable: true, render: (r) => when(r.created_at) },
  {
    key: 'customer_name',
    header: 'From',
    render: (r) => (
      <span className="privacy__result">
        {r.channel === 'partner' ? `Partner: ${r.dealer_name ?? '—'}` : r.customer_name ?? 'Customer (name erased)'}
        <br />
        {r.customer_email ?? '—'}
      </span>
    ),
  },
  { key: 'claim_type', header: 'Type', sortable: true, render: (r) => TYPE_LABEL[r.claim_type] ?? r.claim_type },
  { key: 'status', header: 'Status', render: (r) => statusTag(r.status) },
  { key: 'respond_by', header: 'Due', render: dueTag },
  {
    key: 'items',
    header: 'What',
    render: (r) => (
      <span className="privacy__result">
        {r.items.map((i) => `${i.quantity ?? 1} x ${i.description ?? 'filter'}`).join(', ')}
        {r.photo_count > 0 ? <><br />{r.photo_count} photo{r.photo_count === 1 ? '' : 's'}</> : null}
      </span>
    ),
  },
  { key: 'refunded_usd', header: 'Refunded', numeric: true, render: (r) => (r.refunded_usd > 0 ? money(r.refunded_usd) : '—') },
];

const FILTERS: FilterDef<ClaimRow>[] = [
  {
    key: 'status',
    label: 'Status',
    options: [{ value: 'all', label: 'All' }, ...(Object.keys(STATUS_LABEL) as Status[]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))],
    match: (r, v) => r.status === v,
  },
  {
    key: 'channel',
    label: 'From',
    options: [
      { value: 'all', label: 'All' },
      { value: 'consumer', label: 'Customers' },
      { value: 'partner', label: 'Partners' },
    ],
    match: (r, v) => r.channel === v,
  },
];

function Counts() {
  const q = useQuery({
    queryKey: ['mcp', 'listClaims', 'counts'],
    queryFn: async () => (await mcpCall<{ statusCounts: Record<Status, number> }>('listClaims', { limit: 1 })).statusCounts,
  });
  if (!q.data) return null;
  return (
    <dl className="privacy__counts" aria-label="Claims by status">
      {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
        <div className="privacy__count" key={s}>
          <dt>{STATUS_LABEL[s]}</dt>
          <dd>{q.data[s] ?? 0}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One action form: a title, what it does, optional fields, and a button. */
function ActionForm({
  title,
  desc,
  button,
  onSubmit,
  children,
  disabled,
}: {
  title: string;
  desc: string;
  button: string;
  onSubmit: () => Promise<void>;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || disabled) return;
    setBusy(true);
    try {
      await onSubmit();
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="settings__row" onSubmit={submit}>
      <div className="settings__meta">
        <p className="settings__key">{title}</p>
        <p className="settings__desc">{desc}</p>
      </div>
      <div className="account__form">
        {children}
        <button type="submit" className="settings__save" disabled={busy || disabled}>
          {busy ? 'Working…' : button}
        </button>
      </div>
    </form>
  );
}

function ClaimPanel({ id, onClose }: { id: number; onClose: () => void }) {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['mcp', 'getClaim', id], queryFn: () => mcpCall<ClaimDetail>('getClaim', { id }) });
  const [note, setNote] = useState('');
  const [returnRequired, setReturnRequired] = useState<boolean | null>(null);
  const [cabanaError, setCabanaError] = useState<boolean | null>(null);
  const [amount, setAmount] = useState('');

  if (q.isPending) return <p className="settings__desc">Loading claim #{id}…</p>;
  if (q.error || !q.data) return <p className="privacy__error">Could not load claim #{id}: {(q.error as Error)?.message}</p>;
  const { claim: c, decisions, photos, remedies, payment } = q.data;
  const has = (a: Decision['action']) => decisions.some((d) => d.action === a);
  const verdict = decisions.find((d) => d.action === 'approved' || d.action === 'denied') ?? null;
  const approved = verdict?.action === 'approved';

  async function run(action: string, args: Record<string, unknown>, done: (r: Record<string, unknown>) => string) {
    try {
      const r = await mcpWrite<Record<string, unknown>>(action, { id, ...args });
      const warn = r.emailed === false ? ` The customer was NOT emailed: ${String(r.emailProblem)}.` : '';
      const tax = r.taxReversalProblem ? ` Tax: ${String(r.taxReversalProblem)}.` : '';
      notify({ variant: warn || tax ? 'error' : 'success', message: `${done(r)}${warn}${tax}` });
      setNote('');
      setAmount('');
      await queryClient.invalidateQueries({ queryKey: ['mcp', 'getClaim', id] });
      await queryClient.invalidateQueries({ queryKey: ['mcp', 'listClaims'] });
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    }
  }

  const defaultReturn = c.claim_type === 'return_unopened' || c.claim_type === 'wrong_item';
  const defaultError = c.claim_type !== 'return_unopened';

  return (
    <div className="settings__list">
      <div className="settings__row">
        <div className="settings__meta">
          <p className="settings__key">
            Claim #{c.id}: {c.type_label} {statusTag(c.status)}
          </p>
          <p className="settings__desc">
            {c.channel === 'partner' ? `Partner ${c.dealer_name ?? ''}, order #${c.dealer_order_id}` : `${c.customer_name ?? 'Customer'}, box ${c.box_number ?? '—'} (shipment #${c.shipment_id})`}
            {' · '}{c.customer_email ?? 'no email on file'}
            <br />
            Filed {when(c.created_at)} · delivered {c.delivered_on ?? '— (lost)'} · in time until {c.window_ends_on} · reply by {q.data.respond_by}
            {q.data.refund_by ? ` · refund by ${q.data.refund_by}` : ''}
            <br />
            {c.items.map((i) => `${i.quantity ?? 1} x ${i.description ?? 'filter'}`).join(', ')}
            {c.ordered_description ? <><br />Ordered: {c.ordered_description} · Arrived: {c.received_description}</> : null}
            <br />
            “{c.description}”{c.preferred_remedy ? ` · prefers ${c.preferred_remedy}` : ''}
            <br />
            Paid {money(payment.paid_usd)} ({payment.payment_intent ?? 'no Stripe payment'}) · refunded {money(payment.refunded_usd)} · suggested refund{' '}
            {money(payment.suggested_refund_usd)}
          </p>
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {photos.map((p) =>
                p.url ? (
                  <a key={p.id} href={p.url} target="_blank" rel="noreferrer noopener">
                    <img src={p.url} alt={`Claim ${c.id} photo ${p.id}`} style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 6 }} />
                  </a>
                ) : (
                  <span key={p.id} className="privacy__error">Photo {p.id}: {p.url_problem}</span>
                ),
              )}
            </div>
          )}
          {decisions.length > 0 && (
            <ul className="privacy__result" style={{ marginTop: 8 }}>
              {decisions.map((d) => (
                <li key={d.id}>
                  {when(d.decided_at)} · {ACTION_LABEL[d.action]}
                  {d.action === 'approved' ? ` (${d.return_required ? 'return required' : 'no return'}, ${d.cabana_error ? "Cabana's error" : "customer's return"})` : ''}
                  {d.refund_amount_usd !== null ? ` ${money(d.refund_amount_usd)} (${d.stripe_refund_id}${d.tax_reversal_id ? `, tax reversal ${d.tax_reversal_id}` : ''})` : ''}
                  {d.tax_reversal_note ? ` · ${d.tax_reversal_note}` : ''}
                  {d.label_tracking_number ? ` ${d.label_carrier} ${d.label_tracking_number} (${money(d.label_cost_usd)})` : ''}
                  {d.replacement_shipment_id ? ` shipment #${d.replacement_shipment_id}` : ''}
                  {d.replacement_dealer_order_id ? ` order #${d.replacement_dealer_order_id}` : ''}
                  {d.note ? ` · “${d.note}”` : ''}
                  {d.email_message_id ? ' · emailed' : ' · not emailed'}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="account__form">
          <button type="button" className="settings__save" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {!verdict && can('decideClaim') && (
        <>
          <ActionForm
            title="Approve"
            desc="Emails the customer. When the filters must come back, the email gives the return address (the Alabama warehouse; while that is not set, it tells them to wait for it); when the error is Cabana's, it says a prepaid label follows."
            button="Approve claim"
            onSubmit={() =>
              run(
                'decideClaim',
                {
                  decision: 'approve',
                  returnRequired: returnRequired ?? defaultReturn,
                  cabanaError: cabanaError ?? defaultError,
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                (r) =>
                  `Claim #${id} approved (${r.status}).` +
                  (r.returnAddressMissing ? ' The Alabama warehouse address is not set, so the customer was told the address will follow.' : ''),
              )
            }
          >
            <label className="privacy__field">
              <span>
                <input
                  type="checkbox"
                  checked={returnRequired ?? defaultReturn}
                  disabled={c.claim_type === 'return_unopened' || c.claim_type === 'lost'}
                  onChange={(e) => setReturnRequired(e.target.checked)}
                />{' '}
                Filters must come back
              </span>
            </label>
            <label className="privacy__field">
              <span>
                <input type="checkbox" checked={cabanaError ?? defaultError} onChange={(e) => setCabanaError(e.target.checked)} /> Cabana&rsquo;s error
                (we pay return shipping)
              </span>
            </label>
          </ActionForm>
          <ActionForm
            title="Deny"
            desc="The note is emailed to the customer as the reason, so write it for them."
            button="Deny claim"
            disabled={!note.trim()}
            onSubmit={() => run('decideClaim', { decision: 'deny', note: note.trim() }, () => `Claim #${id} denied.`)}
          />
        </>
      )}

      {approved && verdict?.return_required && verdict?.cabana_error && !has('label_sent') && !has('return_received') && can('sendClaimReturnLabel') && (
        <ActionForm
          title="Send a prepaid return label"
          desc="Buys the lowest-rate EasyPost label from the customer's address to the Alabama warehouse's ship-from address, and emails it; refused while that address is not set. Once per claim."
          button="Buy and email label"
          onSubmit={() => run('sendClaimReturnLabel', {}, (r) => `Label ${String(r.carrier)} ${String(r.trackingNumber)} sent ($${Number(r.costUsd).toFixed(2)}).`)}
        />
      )}

      {approved && verdict?.return_required && !has('return_received') && can('recordClaimReturnReceived') && (
        <ActionForm
          title="Return received and checked"
          desc="Starts the 10-business-day refund clock the terms promise, and tells the customer."
          button="Record return received"
          onSubmit={() => run('recordClaimReturnReceived', note.trim() ? { note: note.trim() } : {}, (r) => `Return recorded; refund due by ${String(r.refundBy)}.`)}
        />
      )}

      {approved && remedies.refund && (!verdict?.return_required || has('return_received')) && can('refundClaim') && payment.payment_intent && (
        <ActionForm
          title="Refund"
          desc={`Refunds through Stripe to the original card and reverses the sales tax for the amount. Leave blank for the suggested ${money(payment.suggested_refund_usd)}.`}
          button="Refund"
          onSubmit={() =>
            run(
              'refundClaim',
              { ...(amount.trim() ? { amountUsd: Number(amount) } : {}), ...(note.trim() ? { note: note.trim() } : {}) },
              (r) => `Refunded ${money(Number(r.amountUsd))} (${String(r.refundId)}).`,
            )
          }
        >
          <label className="privacy__field">
            Amount (USD)
            <input className="settings__input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(payment.suggested_refund_usd)} />
          </label>
        </ActionForm>
      )}

      {approved && remedies.replacement && !has('replaced') && can('replaceClaim') && (
        <ActionForm
          title="Send a replacement"
          desc={
            c.channel === 'partner'
              ? 'Creates a zero-charge order with the claimed products, to the original ship-to address, in the order queue.'
              : 'Creates a zero-charge shipment with the claimed filters and queues it to the fulfilment node. The next renewal is unchanged.'
          }
          button="Send replacement"
          onSubmit={() => run('replaceClaim', note.trim() ? { note: note.trim() } : {}, (r) => `Replacement ${r.replacementShipmentId ? `shipment #${String(r.replacementShipmentId)}` : `order #${String(r.replacementDealerOrderId)}`} created.`)}
        />
      )}

      {c.status !== 'denied' && c.status !== 'resolved' && (
        <div className="settings__row">
          <div className="settings__meta">
            <p className="settings__key">Note</p>
            <p className="settings__desc">Used by the next action: the reason for a denial (sent to the customer), or a note on an approval, receipt, refund or replacement.</p>
          </div>
          <div className="account__form">
            <textarea className="settings__input" rows={3} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}

export function Claims() {
  const [selected, setSelected] = useState<number | null>(null);
  return (
    <>
      {selected !== null && <ClaimPanel id={selected} onClose={() => setSelected(null)} />}
      <Counts />
      <RecordsTable<ClaimRow>
        title="Claims"
        intro="Returns of unopened product, wrong items, damaged and lost boxes, and warranty claims, from customers (cabanafilters.com/account/returns) and partners (the partner portal). The terms promise a reply within 10 business days and a refund within 10 business days of receiving a return. Open a claim to see its photos and act on it; every action is emailed to the customer."
        tool="listClaims"
        args={{ limit: 500 }}
        select={(r) => (r as { claims: ClaimRow[] }).claims ?? []}
        columns={COLUMNS}
        rowKey={(r) => r.id}
        emptyMessage="No claims yet."
        refreshable
        searchable={{
          placeholder: 'Search name, email, partner or claim number…',
          match: (r, q) => [String(r.id), r.customer_name, r.customer_email, r.dealer_name].some((f) => f != null && f.toLowerCase().includes(q)),
        }}
        filters={FILTERS}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        actions={{
          header: '',
          render: (r) => (
            <button type="button" className="settings__save" onClick={() => setSelected(r.id)}>
              Open
            </button>
          ),
        }}
      />
    </>
  );
}
